import axios from 'axios';
import { API_CONFIG } from '../constants/index.js';

/**
 * Platform Service
 * Handles API calls for platform-wide statistics and information
 */
class PlatformService {
  constructor() {


    this.baseURL = API_CONFIG.BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';
    this.platformEndpoint = API_CONFIG.ENDPOINTS.PLATFORM;


  }

  /**
   * Fetch platform statistics for homepage
   * @returns {Promise<object>} Platform stats data
   */
  async fetchPlatformStats() {
    try {
      const baseURL = API_CONFIG.BASE_URL || 'http://localhost:5000';
      const platformEndpoint = API_CONFIG.ENDPOINTS.PLATFORM || '/api/platform';

      //console.log('Making request to:', `${baseURL}${platformEndpoint}/stats`);

      const response = await axios.get(`${baseURL}${platformEndpoint}/stats`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error?.message || 'Failed to fetch platform statistics');
      }
    } catch (error) {
      console.error('Platform stats fetch error:', error);

      // Return fallback data on error
      return this.getFallbackStats();
    }
  }

  /**
   * Get fallback statistics when API fails
   * @returns {object} Fallback stats
   */
  getFallbackStats() {
    return {
      platform: {
        projectsFunded: 0,
        totalRaised: {
          pi: "0",
          ngn: "0"
        },
        activeDonors: 0,
        totalUsers: 0,
        successRate: 0
      },
      featured: {
        activeProject: null,
        totalSupporters: 0
      },
      cached: false,
      fallback: true
    };
  }

  /**
   * Format stats for display
   * @param {object} stats - Raw stats from API
   * @returns {object} Formatted stats
   */
  formatStats(stats) {
    if (!stats) return this.getFallbackStats();

    return {
      ...stats,
      platform: {
        ...stats.platform,
        projectsFunded: this.formatNumber(stats.platform.projectsFunded),
        totalRaised: {
          pi: this.formatCurrency(stats.platform.totalRaised?.pi || 0),
          ngn: this.formatCurrency(stats.platform.totalRaised?.ngn || 0)
        },
        activeDonors: this.formatNumber(stats.platform.activeDonors),
        totalUsers: this.formatNumber(stats.platform.totalUsers),
        successRate: `${stats.platform.successRate}%`
      },
      featured: {
        ...stats.featured,
        activeProject: stats.featured.activeProject ? {
          ...stats.featured.activeProject,
          currentFunding: this.formatCurrency(stats.featured.activeProject.currentFunding)
        } : null,
        totalSupporters: this.formatNumber(stats.featured.totalSupporters)
      }
    };
  }

  /**
   * Format numbers with commas
   * @param {number} num - Number to format
   * @returns {string} Formatted number
   */
  formatNumber(num) {
    if (!num && num !== 0) return "0";
    return num.toLocaleString();
  }

  /**
   * Format currency values
   * @param {string|number} amount - Amount to format
   * @returns {string} Formatted currency
   */
  formatCurrency(amount) {
    if (!amount && amount !== 0) return "0";
    const num = parseFloat(amount);

    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    } else {
      return num.toLocaleString();
    }
  }

  /**
   * Invalidate platform stats cache (admin only)
   * @returns {Promise<object>} Response
   */
  async invalidateCache() {
    try {
      const response = await axios.post(`${this.baseURL}${this.platformEndpoint}/stats/invalidate`);

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.error?.message || 'Failed to invalidate cache');
      }
    } catch (error) {
      console.error('Cache invalidation error:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const platformService = new PlatformService();
export default platformService;